import { useEffect, useRef } from 'react'
import { MessagePayload, onMessage } from 'firebase/messaging'
import { PlataformaType } from '_metronic/helpers'
import { useAuth } from 'app/modules/auth'
import { getFirebaseMessaging, isFirebaseConfigured } from './firebaseApp'
import { getPushFid, onPushFidChange } from './getPushFid'
import { getStoredPushToken, setStoredPushToken } from './pushTokenStorage'
import { registrarPushToken, revogarPushToken } from './_requests'

/**
 * Envia o FID deste navegador ao backend e, se ele mudou, revoga o identificador anterior —
 * seja um FID antigo (rotação) ou o token FCM legado de antes da migração para FID, que
 * senão continuaria recebendo push em paralelo.
 */
const sincronizarFid = async (fid: string) => {
  const anterior = getStoredPushToken()

  try {
    await registrarPushToken(fid, PlataformaType.Site)
    setStoredPushToken(fid)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Não foi possível registrar o dispositivo para notificações push.', error)
    return
  }

  if (anterior && anterior !== fid) {
    try {
      await revogarPushToken(anterior)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Não foi possível revogar o identificador push anterior.', error)
    }
  }
}

/**
 * Registra o FID deste dispositivo no backend e escuta mensagens recebidas em primeiro plano
 * (app aberto). Qualquer falha é silenciosa — push é um "extra", nunca deve impedir o uso do app.
 *
 * O registro é refeito a cada início de sessão, sem confiar no valor guardado no localStorage:
 * o backend pode ter removido o registro por conta própria (logout em outro dispositivo, FCM
 * devolvendo UNREGISTERED), e o `PUT` é idempotente.
 *
 * @param enabled Controla se o hook deve registrar/escutar. Fica em React state (não lê
 * `getPushOptOut()` sozinho) pra que ligar/desligar o toggle reaja na hora, sem reload.
 */
export const usePushNotifications = (
  onForegroundMessage: (payload: MessagePayload) => void,
  enabled: boolean
) => {
  const { auth } = useAuth()
  const callbackRef = useRef(onForegroundMessage)
  callbackRef.current = onForegroundMessage

  useEffect(() => {
    if (!enabled || !auth?.accessToken || !isFirebaseConfigured) return

    let cancelado = false
    let pararDeEscutar: (() => void) | undefined
    let pararDeOuvirFid: (() => void) | undefined

    const configurar = async () => {
      const fid = await getPushFid()
      if (cancelado) return

      if (fid) await sincronizarFid(fid)
      if (cancelado) return

      // Rotação de FID feita pelo SDK fora deste fluxo.
      pararDeOuvirFid = onPushFidChange((novoFid) => {
        if (novoFid !== getStoredPushToken()) sincronizarFid(novoFid)
      })

      const messaging = await getFirebaseMessaging()
      if (!messaging || cancelado) return

      pararDeEscutar = onMessage(messaging, (payload) => callbackRef.current(payload))
    }

    configurar()

    return () => {
      cancelado = true
      pararDeOuvirFid?.()
      pararDeEscutar?.()
    }
  }, [auth?.accessToken, enabled])
}
